import { Injectable, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';

interface PDFDocument {
    save: (filename: string) => void;
    output: (type: 'blob' | 'arraybuffer' | 'uint8array') => Blob | ArrayBuffer | Uint8Array;
    internal?: { getNumberOfPages: () => number };
    setPage?: (page: number) => void;
    setFontSize?: (size: number) => void;
    setTextColor?: (color: number) => void;
    text?: (text: string, x: number, y: number, options?: any) => void;
}

type PDFData = PDFDocument | Blob;

@Injectable({
    providedIn: 'root'
})
export class PDFSaveService {
    private platform = inject(Platform);

    async savePDF(pdfData: PDFData, fileName: string): Promise<void> {
        const cleanFileName = fileName.replace(/[/\\]/g, '') + (fileName.endsWith('.pdf') ? '' : '.pdf');

        if (this.platform.is('capacitor') || this.platform.is('cordova')) {
            const blob = this.getPDFAsBlob(pdfData);
            await this.saveBlobMobile(blob, cleanFileName);
        } else {
            if (pdfData instanceof Blob) {
                const url = URL.createObjectURL(pdfData);
                const link = document.createElement('a');
                link.href = url;
                link.download = cleanFileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                pdfData.save(cleanFileName);
            }
        }
    }

    private getPDFAsBlob(pdfData: PDFData): Blob {
        if (pdfData instanceof Blob) return pdfData;
        return pdfData.output('blob') as Blob;
    }

    private async saveBlobMobile(blob: Blob, fileName: string): Promise<void> {
        try {
            const base64Data = await this.getBase64DataFromBlob(blob);

            const writeFileResponse = await Filesystem.writeFile({
                path: fileName,
                data: base64Data,
                directory: Directory.Library
            });

            try {
                await FileOpener.open({
                    filePath: writeFileResponse.uri,
                    contentType: 'application/pdf',
                    openWithDefault: true
                });
            } catch (e) {
                console.log('Error opening file, but file was saved', e);
            }
        } catch (error) {
            throw new Error(`Failed to save PDF: ${error.message || error}`);
        }
    }

    private async getBase64DataFromBlob(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
}
